import React, { useState, useEffect, useCallback, useRef } from "react";
import { dataGridSurface } from "../common/tableStyles";
import { Box, styled, Chip, TextField, InputAdornment, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridSortModel,
} from "@mui/x-data-grid";
import IProduct from "../../entities/IProduct";
import ProductService from "../../services/ProductService";
import ProductActions from "./ProductActions";
import { unwrapPage } from "../../services/apiUtils";
import { isProductPublished } from "../../services/productStatus";
import productEvents from "../../services/productEvents";
import DataGridLoadingOverlay from "../common/DataGridLoadingOverlay";
import useCategoryLookup from "../../hooks/useCategoryLookup";
import { extractCategoryId } from "../../services/productTypeUtils";


const StyledTable = styled(Box)(({ theme }) => ({
  ...dataGridSurface(theme),
  margin: "16px 0 0 0",
  height: 560,
  "& .product-row--published": {
    backgroundColor: "transparent",
  },
  "& .product-row--published:hover": {
    backgroundColor: "#F8FAFC",
  },
}));

const ProductTableToolbar: React.FC = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
  </GridToolbarContainer>
);

const ProductTable: React.FC = () => {
  const categoryLookup = useCategoryLookup();
  const [productData, setProductData] = useState<Array<IProduct>>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");


  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "productId", sort: "desc" },
  ]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);



  const isIdSearch = /^\d+$/.test(searchText);

  const loadProducts = useCallback(() => {
    setLoading(true);
    const activeSort = sortModel[0];
    ProductService.filterProducts({
      page,
      size: pageSize,
      ...(searchText
        ? isIdSearch
          ? { productId: searchText }
          : { title: searchText }
        : {}),
      ...(activeSort
        ? { sortBy: activeSort.field, sortDirection: activeSort.sort === "asc" ? "Asc" : "Desc" }
        : {}),
    })
      .then((res: any) => {
        const { content, totalElements } = unwrapPage<IProduct>(res);




        const sorted = activeSort
          ? [...content].sort((a: any, b: any) => {
              const field = activeSort.field;
              const av = a?.[field];
              const bv = b?.[field];
              let cmp = 0;
              if (typeof av === "number" && typeof bv === "number") {
                cmp = av - bv;
              } else {
                cmp = String(av ?? "").localeCompare(String(bv ?? ""));
              }
              return activeSort.sort === "asc" ? cmp : -cmp;
            })
          : content;
        setProductData(sorted);
        setTotalCount(totalElements);
      })
      .catch(() => {
        setProductData([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [page, pageSize, searchText, isIdSearch, sortModel]);

  const handleSortModelChange = (newModel: GridSortModel) => {
    setPage(0);
    setSortModel(newModel);
  };

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {




    return productEvents.onProductChanged(() => {
      setPage(0);
      setSortModel([{ field: "productId", sort: "desc" }]);
      loadProducts();
    });
  }, [loadProducts]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      setSearchText(searchInput.trim());
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const columns: GridColDef[] = [
    { field: "productId", headerName: "ID", width: 80 },
    { field: "product_type", headerName: "Type", width: 110 },
    {
      field: "categoryId",
      headerName: "Category",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) =>
        params.row?.categoryName ||
        params.row?.category?.title ||
        categoryLookup[extractCategoryId(params.row)] ||
        "-",
    },
    {
      field: "title",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "price",
      headerName: "Price",
      flex: 1,
    },
    {
      field: "stock",
      headerName: "Stock",
      flex: 1,
    },
    {
      field: "vendorId",
      headerName: "vendorId",
      flex: 1,
    },
    {
      field: "estatusId",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={isProductPublished(params.row) ? "Published" : "Draft"}
          color={isProductPublished(params.row) ? "success" : "warning"}
          variant={isProductPublished(params.row) ? "filled" : "outlined"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 210,
      type: "actions",
      renderCell: (params) => (
        <ProductActions product={params.row} onDeleted={loadProducts} />
      ),
    },
  ];

  return (
    <StyledTable>
      <Box sx={{ mb: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <TextField
          size="small"
          sx={{ maxWidth: 360, flex: 1, minWidth: 220 }}
          placeholder="Search by product ID or name..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <DataGrid
        sx={{ backgroundColor: "transparent" }}
        rows={productData}
        columns={columns}
        getRowId={(row: any) => row.productId}
        getRowClassName={(params) =>
          isProductPublished(params.row) ? "product-row--published" : ""
        }
        components={{ Toolbar: ProductTableToolbar, LoadingOverlay: DataGridLoadingOverlay }}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        rowCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        checkboxSelection
        disableSelectionOnClick
      />
    </StyledTable>
  );
};

export default ProductTable;
